import { formatBrlCurrency } from "./price";

const WHATSAPP_FORMAT_CHARS = /[*_~`]/g;

function sanitizeWhatsAppText(text: string): string {
  return text.replace(WHATSAPP_FORMAT_CHARS, "").replace(/\s+/g, " ").trim();
}

export const DEFAULT_WHATSAPP_PRODUCT_MESSAGE_TEMPLATE = `Olá! Tenho interesse neste *produto*:

*Produto:* {{product_name}}
*Preço:* {{product_price}}

*Ver no catálogo:*
{{product_url}}

*Forma de pagamento:* {{payment_method}}

_Pode me passar mais informações?_`;

export const DEFAULT_WHATSAPP_CART_MESSAGE_TEMPLATE = `Olá! Tenho interesse nestes *produtos*:

{{#products}}
• {{product_name}} · {{product_quantity}} · {{product_price}} · {{product_total}}
{{/products}}

──────────────────

*Total aproximado:* *{{cart_total}}*

*Forma de pagamento:* {{payment_method}}

_Pode me passar mais informações?_`;

/** Line item fields available inside {{#products}}...{{/products}} */
export const CART_LINE_TEMPLATE_VARIABLES = [
  { key: "product_name", label: "Nome do produto" },
  { key: "product_quantity", label: "Quantidade" },
  { key: "product_price", label: "Preço unitário" },
  { key: "product_total", label: "Total da linha" },
] as const;

export const WHATSAPP_TEMPLATE_VARIABLES = [
  { key: "store_name", label: "Nome da loja", scopes: ["product", "cart"] as const },
  { key: "product_name", label: "Nome do produto", scopes: ["product"] as const },
  { key: "product_price", label: "Preço do produto", scopes: ["product"] as const },
  { key: "product_url", label: "Link do produto", scopes: ["product"] as const },
  {
    key: "cart_items",
    label: "Lista formatada (legado)",
    scopes: ["cart"] as const,
  },
  { key: "cart_total", label: "Total do carrinho", scopes: ["cart"] as const },
  { key: "payment_method", label: "Forma de pagamento", scopes: ["product", "cart"] as const },
] as const;

export interface CartLineTemplateItem {
  name: string;
  priceCents: number;
  quantity?: number;
}

const VARIABLE_PATTERN = /\{\{(\w+)\}\}/g;
const PRODUCTS_BLOCK_PATTERN = /\{\{#?products\}\}([\s\S]*?)\{\{\/products\}\}/g;

function renderLineTemplate(
  lineTemplate: string,
  item: CartLineTemplateItem,
  globalVariables: Record<string, string>
): string {
  const quantity = item.quantity ?? 1;
  const lineVariables: Record<string, string> = {
    ...globalVariables,
    product_name: sanitizeWhatsAppText(item.name),
    product_quantity: String(quantity),
    product_price: formatBrlCurrency(item.priceCents),
    product_total: formatBrlCurrency(item.priceCents * quantity),
  };

  return lineTemplate.replace(VARIABLE_PATTERN, (_, key: string) => lineVariables[key] ?? "");
}

function renderProductBlocks(
  source: string,
  products: CartLineTemplateItem[],
  globalVariables: Record<string, string>
): string {
  return source.replace(PRODUCTS_BLOCK_PATTERN, (_, lineTemplate: string) => {
    if (products.length === 0) return "";
    return products
      .map((item) => renderLineTemplate(lineTemplate.trim(), item, globalVariables))
      .join("\n");
  });
}

export function renderMessageTemplate(
  template: string | null | undefined,
  variables: Record<string, string>,
  fallbackTemplate: string,
  products: CartLineTemplateItem[] = []
): string {
  const source = template?.trim() || fallbackTemplate;

  const withProducts = renderProductBlocks(source, products, variables);

  return withProducts
    .replace(VARIABLE_PATTERN, (_, key: string) => variables[key] ?? "")
    .replace(/\n{3,}/g, "\n\n");
}
